import { AloftPrice, orUndefined, schAloftPrice } from '@aloft/toolkit-shared';
import { Flex, InfoIcon, SimpleDollarInput, Typography } from '@aloft/toolkit-sui';

import { useEffect } from 'react';
import { Control, Controller, Form, FormProvider, SubmitHandler } from 'react-hook-form';

import { FormTextarea } from '../../../components/FormTextarea/FormTextarea.tsx';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { submitReconciliationDataSheet } from '../../../redux/features/reconciliationSlice.ts';
import { selectCommentary } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import {
  selectIsArvOrder,
  selectIsMockOrder,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectReconciliation } from '../../../redux/features/selectors/selectReconciliation.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { useReconciliationSheetForm } from './hooks/useReconciliationSheetForm.ts';
import { ReconciliationSheetFormOutput } from './schemas.ts';

type ReconciliationFieldProps = {
  control: Control;
  fieldName: keyof ReconciliationSheetFormOutput;
  label: string;
  handleChange: () => void;
  isRequired: boolean;
  tooltipContent?: string;
};

const ReconciliationField = ({
  control,
  fieldName,
  label,
  handleChange,
  isRequired,
  tooltipContent,
}: ReconciliationFieldProps) => {
  const validation = useAppraisifyValidation();
  const error = validation.errors.Reconciliation[fieldName];

  return (
    <Flex flexDirection="column" flex={1}>
      <Flex alignItems="center" gap="4px" marginBottom="5px">
        <Typography
          variant="h3"
          color="textSecondary"
          __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
        >
          {label}
          <span className="simple-field-required" style={{ marginLeft: '2px' }}>
            {isRequired ? '*' : ''}
          </span>
        </Typography>
        {tooltipContent && <InfoIcon tooltipContent={tooltipContent} />}
      </Flex>
      <Controller
        name={fieldName}
        control={control}
        render={({ field, fieldState }) => (
          <SimpleDollarInput
            value={field.value ? field.value.toString(true, true) : ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value ? AloftPrice.fromDollarLike(e.target.value) : undefined;
              field.onChange(value);
              handleChange();
            }}
            onBlur={field.onBlur}
            required={isRequired}
            placeholder={isRequired ? 'Required' : ''}
            errorMessage={fieldState.isTouched ? error?.message : undefined}
            invalid={fieldState.invalid && fieldState.isTouched}
          />
        )}
      />
    </Flex>
  );
};

export const ReconciliationSheet = () => {
  const dispatch = useAppDispatch();
  const form = useReconciliationSheetForm();
  const validation = useAppraisifyValidation();
  const {
    reconciliationCommentary: reconciliationCommentaryError,
    aivReconciliationCommentary: aivReconciliationCommentaryError,
    arvReconciliationCommentary: arvReconciliationCommentaryError,
  } = validation.errors.Reconciliation;

  const { reconciliation: reconciliationCommentary } = useAppSelector(selectCommentary);
  const reconciliation = useAppSelector(selectReconciliation);
  const hasArvOption = useAppSelector(selectIsArvOrder);
  const isMockOrder = useAppSelector(selectIsMockOrder);

  useEffect(() => {
    form.reset({
      reconciliationCommentary: reconciliationCommentary?.reconciliationCommentary ?? '',
      aivReconciliationCommentary:
        reconciliationCommentary?.aivReconciliationCommentary ??
        reconciliationCommentary?.reconciliationCommentary ??
        '',
      arvReconciliationCommentary: reconciliationCommentary?.arvReconciliationCommentary ?? '',
      totalCostToCure: orUndefined(schAloftPrice.parse)(reconciliation.totalCostToCure),
      totalRenovationBudget: orUndefined(schAloftPrice.parse)(reconciliation.totalRenovationBudget),
      opinionOfMarketValue: orUndefined(schAloftPrice.parse)(reconciliation.opinionOfMarketValue),
      opinionOfAfterRepairValue: orUndefined(schAloftPrice.parse)(
        reconciliation.opinionOfAfterRepairValue,
      ),
    });
  }, [reconciliation, reconciliationCommentary]);

  const onSubmit: SubmitHandler<ReconciliationSheetFormOutput> = (data) => {
    if (hasArvOption) {
      dispatch(
        submitReconciliationDataSheet({
          ...data,
          reconciliationCommentary: data.aivReconciliationCommentary,
        }),
      );
    } else {
      dispatch(
        submitReconciliationDataSheet({
          ...data,
          aivReconciliationCommentary: undefined,
          arvReconciliationCommentary: undefined,
        }),
      );
    }
  };

  const handleChange = () => {
    form
      .handleSubmit(onSubmit, undefined)()
      .catch(() => {});
  };

  return (
    <Flex flexDirection="column" marginLeft="2rem" marginRight="4rem" height="100%">
      <Typography variant="h1">Reconciliation</Typography>
      <FormProvider {...form}>
        <Form onChange={handleChange} style={{ height: '100%' }}>
          <Flex marginTop="20px" gap={4} flexWrap="wrap">
            {hasArvOption && (
              <>
                <ReconciliationField
                  control={form.control}
                  fieldName="totalRenovationBudget"
                  label="Total Renovation Budget"
                  handleChange={handleChange}
                  isRequired={!isMockOrder}
                />
                <ReconciliationField
                  control={form.control}
                  fieldName="opinionOfMarketValue"
                  label="Opinion of As Is Value"
                  handleChange={handleChange}
                  isRequired={!isMockOrder}
                />
                <ReconciliationField
                  control={form.control}
                  fieldName="opinionOfAfterRepairValue"
                  label="Opinion of After Repair Value"
                  handleChange={handleChange}
                  isRequired={!isMockOrder}
                />
              </>
            )}
            {!hasArvOption && (
              <>
                {reconciliation?.includeTotalCostToCure && (
                  <ReconciliationField
                    control={form.control}
                    fieldName="totalCostToCure"
                    label="Total Cost to Cure"
                    handleChange={handleChange}
                    isRequired={!isMockOrder}
                    tooltipContent="Major items only (i.e. soundness, security, livability). No cosmetic or minor deferred maintenance."
                  />
                )}
                <ReconciliationField
                  control={form.control}
                  fieldName="opinionOfMarketValue"
                  label="Opinion of Market Value"
                  handleChange={handleChange}
                  isRequired={!isMockOrder}
                />
              </>
            )}
          </Flex>
          <Flex marginTop="20px" flexDirection="column" flex={1} height="80%" gap={4}>
            {hasArvOption ? (
              <>
                <Controller
                  name="aivReconciliationCommentary"
                  control={form.control}
                  render={({ fieldState }) => (
                    <FormTextarea
                      name="aivReconciliationCommentary"
                      control={form.control}
                      label="AIV Reconciliation Commentary"
                      placeholder={isMockOrder ? '' : 'Required'}
                      isRequired={!isMockOrder}
                      column
                      resize="vertical"
                      customErrorMessage={
                        fieldState.isTouched
                          ? aivReconciliationCommentaryError?.message
                          : undefined
                      }
                    />
                  )}
                />
                <Controller
                  name="arvReconciliationCommentary"
                  control={form.control}
                  render={({ fieldState }) => (
                    <FormTextarea
                      name="arvReconciliationCommentary"
                      control={form.control}
                      label="ARV Reconciliation Commentary"
                      placeholder={isMockOrder ? '' : 'Required'}
                      isRequired={!isMockOrder}
                      column
                      resize="vertical"
                      customErrorMessage={
                        fieldState.isTouched
                          ? arvReconciliationCommentaryError?.message
                          : undefined
                      }
                    />
                  )}
                />
              </>
            ) : (
              <Controller
                name="reconciliationCommentary"
                control={form.control}
                render={({ fieldState }) => (
                  <FormTextarea
                    name="reconciliationCommentary"
                    control={form.control}
                    label="Reconciliation Commentary"
                    placeholder={isMockOrder ? '' : 'Required'}
                    isRequired={!isMockOrder}
                    column
                    resize="vertical"
                    customErrorMessage={
                      fieldState.isTouched ? reconciliationCommentaryError?.message : undefined
                    }
                  />
                )}
              />
            )}
          </Flex>
        </Form>
      </FormProvider>
    </Flex>
  );
};
